import localhost from "apis/localhost";
import { useCallback, useEffect, useRef, useState } from "react";

// Both current callers (Proposal Outline, Technical Alignment) are Bedrock-pinned.
const HEALTH_ENDPOINT = "/api/llm/health";
const COOLDOWN_MS = 30000;

const useLlmHealth = () => {
  const [available, setAvailable] = useState(true);
  const [checking, setChecking] = useState(true);
  const lastCheckRef = useRef(0);
  const inFlightRef = useRef(false);

  const fetchHealth = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    try {
      const response = await localhost.get(HEALTH_ENDPOINT);
      setAvailable(response.data?.available ?? true);
      lastCheckRef.current = Date.now();
    } catch (e) {
      // Optimistic: if health endpoint itself fails, assume available
      setAvailable(true);
    } finally {
      inFlightRef.current = false;
      setChecking(false);
    }
  }, []);

  const checkBeforeAction = useCallback(async () => {
    try {
      const response = await localhost.get(HEALTH_ENDPOINT);
      const isAvailable = response.data?.available ?? true;
      setAvailable(isAvailable);
      lastCheckRef.current = Date.now();
      return isAvailable;
    } catch (e) {
      // Optimistic: allow action if health endpoint is unreachable
      return true;
    }
  }, []);

  useEffect(() => {
    fetchHealth();

    const onFocus = () => {
      const elapsed = Date.now() - lastCheckRef.current;
      if (elapsed >= COOLDOWN_MS) {
        fetchHealth();
      }
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchHealth]);

  return { available, checking, checkBeforeAction };
};

export default useLlmHealth;
